import { getAuth } from "firebase/auth";
import { assertCanWrite, isLookOnly } from "./lookOnly";

// The single place the frontend attaches the signed-in user's Firebase ID token
// to a backend call. Cloud Functions verify this token to confirm the caller is
// a real TES user rather than anyone on the internet. Modeled on the one
// existing pattern in clientPortal.js.

// Returns the current user's ID token, or "" when signed out or on any error.
// Never throws — a token failure must not break the request it was decorating.
export const getIdTokenSafe = async () => {
  try {
    const user = getAuth().currentUser;
    return (user && (await user.getIdToken())) || "";
  } catch {
    return "";
  }
};

// fetch() with the Bearer token attached when a user is signed in. When signed
// out, the request goes tokenless and the backend guard will reject it.
//
// In the owner's look-only person view (utils/lookOnly.js) every call that is not a plain
// read (GET) is refused before it leaves, unless it is on the short list below of POSTs
// that only fetch or draft something and change nothing. Failing closed on purpose: a
// new sending or saving endpoint is blocked in that view without anyone listing it.
export const LOOK_ONLY_READ_POSTS = [
  "/fubListContacts",
  "/ai-suggest-reply",
  "/referral/email-draft",
  "/referral/pdf",
  "/oh-requests/preview-emails",
  "/market-news/preview",
];

export const isReadOnlyCall = (url, options = {}) => {
  const method = String(options.method || "GET").toUpperCase();
  if (method === "GET" || method === "HEAD") return true;
  let pathname = "";
  try {
    pathname = new URL(String(url), "https://x.invalid").pathname;
  } catch {
    pathname = String(url || "");
  }
  return LOOK_ONLY_READ_POSTS.some((p) => pathname.endsWith(p));
};

export const authedFetch = async (url, options = {}) => {
  if (isLookOnly() && !isReadOnlyCall(url, options)) assertCanWrite();
  const idToken = await getIdTokenSafe();
  const headers = { ...(options.headers || {}) };
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  return fetch(url, { ...options, headers });
};
